import './App.css';
import './style/styles.css';
import 'swiper/css';
import "swiper/css/navigation";
import { MyContext } from './Context/MyProvider';
import { useContext, useEffect, useState, useMemo } from 'react';
import ShowToast from "./Toast/ShowToast";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Index from './component/auth';
import Forgot from './component/auth/Forgot';
import Header from './component/Header';
import Home from './component/pages/Home';
import secureLocalStorage from 'react-secure-storage';
import OneSignal from 'react-onesignal';
import FindRide from './component/pages/FindRide';
import Profile from './component/pages/Profile';
import MyRides from './component/pages/MyRides';
import Wallet from './component/pages/Wallet';
import Referearn from './component/pages/Referearn';
import Faq from './component/pages/Faq';
import Pages from './component/pages/Pages';
import { socket } from './socket';
import Chat from './component/pages/Chat';
import Category from './component/pages/Category';
import Footer from './component/Footer';
import Notification from './component/pages/Notification';
import i18n from "./component/Language";
import { AnimatePresence, motion } from 'framer-motion';
import ImageLoad from './component/ImageLoad';
import { useTranslation } from 'react-i18next';
import PaymentRes from './component/Payment/PaymentRes';

// Module-level guard to prevent multiple concurrent initializations in Strict Mode
let isOneSignalInitStarted = false;

function App() {
  const { t } = useTranslation();

  const { BaseUrl, showToast, services, ranningData, handleFetchRunningRide, setRaningData, biddingList, setBiddingList } = useContext(MyContext);

  const [isBidding, setIsBidding] = useState(false);
  const [socketStatus, setSocketStatus] = useState("connected"); // "connected", "disconnected", "reconnected"
  const [sortBy, setSortBy] = useState("price"); // "price" or "eta"

  const sortedBids = useMemo(() => {
    let list = [...biddingList];
    if (sortBy === 'price') {
      list.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sortBy === 'eta') {
      list.sort((a, b) => Number(a.tot_min) - Number(b.tot_min));
    }
    return list;
  }, [biddingList, sortBy]);

  useEffect(() => {
    const handleConnect = () => {
      const uid = secureLocalStorage.getItem("Userid");
      if (uid) {
        socket.emit("join", { uid });
      }
      setSocketStatus((prev) => {
        if (prev === "disconnected") {
          setTimeout(() => {
            setSocketStatus("connected");
          }, 2500);
          return "reconnected";
        }
        return "connected";
      });
    };

    const handleDisconnect = () => {
      setSocketStatus("disconnected");
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    if (!socket.connected) {
      setSocketStatus("disconnected");
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  useEffect(() => {
    const initOneSignal = async () => {
      if (isOneSignalInitStarted || (typeof window !== "undefined" && window.OneSignal && window.OneSignal.initialized)) {
        return;
      }
      const appId = secureLocalStorage.getItem("one_app_id");
      if (appId && appId !== "undefined") {
        isOneSignalInitStarted = true; // Synchronously lock the init process
        try {
          console.log("Initializing OneSignal with App ID:", appId);
          await OneSignal.init({
            appId: appId,
            allowLocalhostAsSecureOrigin: true
          });
          // Show slidedown notification prompt automatically
          await OneSignal.showSlidedownPrompt();
        } catch (err) {
          const errMsg = err && err.message ? err.message : String(err);
          if (errMsg.includes("already initialized") || errMsg.includes("Already initialized")) {
            console.log("OneSignal was already initialized. Prompting for notifications.");
            try {
              await OneSignal.showSlidedownPrompt();
            } catch (e) {}
          } else {
            console.error("Error initializing OneSignal:", err);
            isOneSignalInitStarted = false; // Reset guard on failure
          }
        }
      }
    };
    
    // Delay initialization slightly to let settings load
    const timer = setTimeout(initOneSignal, 3000);
    return () => clearTimeout(timer);
  }, []);

  const Location = useLocation();
  const navigate = useNavigate();

  const isShow = Location.pathname.startsWith("/auth/login-signup") || Location.pathname.startsWith("/auth/forgot-password");

  useEffect(() => {
    const uid = secureLocalStorage.getItem("Userid");
    if (uid && socket.connected) {
      socket.emit("join", { uid });
    }
  }, [Location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const uid = secureLocalStorage.getItem("Userid");
    if (!uid) return;

    socket.on(`acceptvehrequest${uid}`, hadnleAcceptVehRequest);
    socket.on(`Vehicle_Bidding${uid}`, handleVehicleBidding);
    socket.on(`Vehicle_Accept_Cancel${uid}`, hadnleVehicleAcceptCancel);
    socket.on(`Vehicle_Ride_Start_End${uid}`, handleVehicleRideStartEnd);
    socket.on(`Vehicle_Ride_Cancel_Client${uid}`, handleVehicleRideCancelClient);
    socket.on(`Vehicle_Ride_Payment${uid}`, handleVehicleRidePayment);
    socket.on(`Vehicle_Ride_Complete_Client${uid}`, handleVehicleRideCompleteClient);

    return () => {
      socket.off(`acceptvehrequest${uid}`, hadnleAcceptVehRequest);
      socket.off(`Vehicle_Bidding${uid}`, handleVehicleBidding);
      socket.off(`Vehicle_Accept_Cancel${uid}`, hadnleVehicleAcceptCancel);
      socket.off(`Vehicle_Ride_Start_End${uid}`, handleVehicleRideStartEnd);
      socket.off(`Vehicle_Ride_Cancel_Client${uid}`, handleVehicleRideCancelClient);
      socket.off(`Vehicle_Ride_Payment${uid}`, handleVehicleRidePayment);
      socket.off(`Vehicle_Ride_Complete_Client${uid}`, handleVehicleRideCompleteClient);
    }
  }, [Location.pathname]);

  const hadnleAcceptVehRequest = (data) => {
    console.log("acceptvehrequest", data);
    if (data) {
      setIsBidding(false);
      navigate("/my-rides", { state: { d_id: data?.uid, id: data?.request_id } });
    }
  }

  const handleVehicleBidding = (data) => {
    if (data?.bidding_list?.length >= 1) {
      setIsBidding(true);
      setBiddingList(prevList => {
        return (data.bidding_list || []).map(newDriver => {
          const existing = prevList.find(d => d.id === newDriver.id);
          if (existing) {
            return {
              ...newDriver,
              received_at: existing.received_at,
              remaining_seconds: existing.remaining_seconds,
              total_seconds: existing.total_seconds
            };
          } else {
            return {
              ...newDriver,
              received_at: Date.now(),
              remaining_seconds: Number(newDriver.diff_second) || 0,
              total_seconds: Number(newDriver.diff_second) || 60
            };
          }
        });
      });
      if (!ranningData || ranningData.length === 0 || !ranningData.price) {
        handleFetchRunningRide();
      }
    } else {
      setIsBidding(false);
      setBiddingList([]);
    }
  }

  useEffect(() => {
    if (biddingList.length === 0) return;

    const timer = setInterval(() => {
      setBiddingList(prevList => {
        const updated = prevList
          .map(driver => {
            const elapsed = Math.floor((Date.now() - (driver.received_at || Date.now())) / 1000);
            const totalSec = driver.total_seconds !== undefined ? driver.total_seconds : driver.diff_second;
            const remaining = (Number(totalSec) || 0) - elapsed;
            return {
              ...driver,
              remaining_seconds: remaining > 0 ? remaining : 0
            };
          })
          .filter(driver => driver.remaining_seconds > 0);

        if (updated.length === 0) {
          setIsBidding(false);
        }
        return updated;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [biddingList.length]);

  useEffect(() => {
    if (ranningData && ranningData.status === "0" && ranningData.bidding_run_status == 1) {
      const uid = secureLocalStorage.getItem("Userid");
      const m_role = ranningData.m_role ? Number(ranningData.m_role) : 1;
      const SocketBody = {
        uid: uid,
        request_id: ranningData.id,
        d_id: ranningData.d_id,
        m_role: m_role
      }
      socket.emit("load_bidding_data", SocketBody);
    }
  }, [ranningData]);

  const hadnleVehicleAcceptCancel = (data) => {
    console.log("Vehicle_Accept_Cancel", data);
    handleFetchRunningRide();
  };

  const handleVehicleRideStartEnd = (data) => {
    console.log("Vehicle_Ride_Start_End", data);
    if (data?.status == "7") {
      setRaningData(null);
    } else {
      handleFetchRunningRide();
    }
  };

  const handleVehicleRideCancelClient = (data) => {
    console.log("Vehicle_Ride_Cancel_Client", data);
    setRaningData(null);
  };

  const handleVehicleRidePayment = (data) => {
    console.log("Vehicle_Ride_Payment", data);
    setRaningData(null);
  };

  const handleVehicleRideCompleteClient = (data) => {
    console.log("Vehicle_Ride_Complete_Client in App", data);
    setRaningData(null);
  };

  const getStatusText = (status) => {
    switch (String(status)) {
      case "1":
        return t("Driver accepted your ride");
      case "2":
        return t("Driver is arriving at pickup");
      case "3":
        return t("Driver has arrived");
      case "5":
        return t("Trip in progress");
      case "6":
        return t("Arrived at destination");
      default:
        return t("Your ride is ongoing");
    }
  };

  const hasOngoingRide = ranningData && ranningData.status !== undefined && ranningData.status !== null && ranningData.status !== "0" && ranningData.status !== "7" && ranningData.status !== "8";
  const isExcludedPath = Location.pathname.startsWith("/my-rides") || Location.pathname.startsWith("/chat");
  const showFloatingCard = hasOngoingRide && !isExcludedPath;

  return (
    <div className="App min-h-screen flex flex-col">

      {!isShow && <Header />}

      <div className="flex-grow">
        <Routes>
          <Route path='/' element={<Home />} />
          <Route path='/auth/login-signup' element={<Index />} />
          <Route path='/auth/forgot-password' element={<Forgot />} />
          <Route path='/find-ride' element={<FindRide />} />
          <Route path='/:title' element={<Category />} />
          <Route path='/pages/:title' element={<Pages />} />

          <Route element={<ProtectedRoute />}>
            <Route path='/profile' element={<Profile />} />
            <Route path='/my-rides' element={<MyRides />} />
            <Route path='/wallet' element={<Wallet />} />
            <Route path='/refer-earn' element={<Referearn />} />
            <Route path='/faq' element={<Faq />} />
            <Route path='/chat' element={<Chat />} />
            <Route path='/notification' element={<Notification />} />
            <Route path='/payment-response' element={<PaymentRes />} />
          </Route>
        </Routes>

      </div>

      {!isShow && <Footer />}

      {showToast && <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[99999]">
        <ShowToast />
      </div>}

      {(() => {
        const showBannerSetting = secureLocalStorage.getItem("socket_status_banner");
        const isBannerEnabled = showBannerSetting === null || showBannerSetting === undefined || showBannerSetting == 1 || showBannerSetting == "1";
        if (isBannerEnabled && (socketStatus === "disconnected" || socketStatus === "reconnected")) {
          return (
            <div className={`socket-status-banner ${socketStatus === "disconnected" ? "socket-offline" : "socket-online"}`}>
              {socketStatus === "disconnected" ? (
                <span>⚠️ Reconnecting to server...</span>
              ) : (
                <span>✓ Connected</span>
              )}
            </div>
          );
        }
        return null;
      })()}

      <AnimatePresence initial={false}>
        {isBidding && <div className="model items-center">
          <motion.div
            key="content"
            initial="close"
            animate="open"
            exit="close"
            variants={{
              open: { opacity: 1, y: 0 },
              close: { opacity: 0, y: 30 },
            }}
            transition={{ duration: 0.3 }}
            className="bg-white w-[450px] px-[20px] py-[15px] rounded-[20px] relative mx-3"
          >

            <div className="flex items-center justify-between mb-2">
              <h1 className='font-medium text-[20px]'>
                Choose a driver
              </h1>
              <button onClick={() => setIsBidding(false)} className='hover:bg-gray-100 rounded-full p-2'>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.414 12L18.707 6.70695C19.098 6.31695 19.098 5.68301 18.707 5.29301C18.316 4.90201 17.684 4.90201 17.293 5.29301L12 10.586L6.70701 5.29301C6.31601 4.90201 5.68401 4.90201 5.29301 5.29301C4.90201 5.68301 4.90201 6.31695 5.29301 6.70695L10.586 12L5.29301 17.293C4.90201 17.683 4.90201 18.3169 5.29301 18.7069C5.48801 18.9019 5.74401 19 6.00001 19C6.25601 19 6.51201 18.9019 6.70701 18.7069L12 13.414L17.293 18.7069C17.488 18.9019 17.744 19 18 19C18.256 19 18.512 18.9019 18.707 18.7069C19.098 18.3169 19.098 17.683 18.707 17.293L13.414 12Z" fill="currentColor" />
                </svg>
              </button>
            </div>

            <p className='text-center'>👋 Driver can offer their fare and time</p>

            <div className="flex justify-between items-center bg-gray-50 p-2 rounded-xl text-sm mb-3">
              <span className="font-semibold text-gray-700">Sort by:</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => setSortBy('price')} 
                  className={`px-3 py-1 rounded-lg font-medium transition ${sortBy === 'price' ? 'bg-[#4038ff] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  Lowest Price
                </button>
                <button 
                  onClick={() => setSortBy('eta')} 
                  className={`px-3 py-1 rounded-lg font-medium transition ${sortBy === 'eta' ? 'bg-[#4038ff] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  Shortest ETA
                </button>
              </div>
            </div>

            <div className="mt-3 space-y-3 max-h-[80vh] scroll-container">
              {sortedBids.map((item, index) => {
                return <RideRequestCard key={item.id} data={item} BaseUrl={BaseUrl} services={services} navigate={navigate} t={t} setIsBidding={setIsBidding} ranningData={ranningData} />
              })}
            </div>

          </motion.div>
        </div>}
      </AnimatePresence>

      {showFloatingCard && (
        <div className="running-ride-floating-card flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-grow min-w-0">
            <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-[--bg-color]">
              {ranningData.d_profile_image ? (
                <img src={BaseUrl + ranningData.d_profile_image} className="w-full h-full object-cover" alt="driver" />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                  D
                </div>
              )}
            </div>
            <div className="min-w-0 flex-grow">
              <h4 className="font-semibold text-[15px] text-slate-800 truncate">
                {ranningData.d_first_name} {ranningData.d_last_name}
              </h4>
              <p className="text-xs text-[--bg-color] font-medium flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-[--bg-color] animate-pulse"></span>
                {getStatusText(ranningData.status)}
              </p>
              <p className="text-[11px] text-gray-500 truncate mt-0.5">
                {ranningData.car_name} • {ranningData.drop_add?.title || ranningData.drop_add?.subtitle || "Active Trip"}
              </p>
            </div>
          </div>
          
          <button 
            onClick={() => navigate("/my-rides", { state: { d_id: ranningData.d_id, id: ranningData.id } })}
            className="px-4 py-2 bg-[--bg-color] hover:bg-[--bg-color-hover] text-white text-xs font-bold rounded-xl transition duration-200 shadow-md shadow-blue-200 flex-shrink-0"
          >
            {t("Track")}
          </button>
        </div>
      )}

    </div>
  );
}
export default App;


const ProtectedRoute = () => {
  const isauth = secureLocalStorage.getItem("isauth") || false;
  return isauth ? <Outlet /> : <Navigate to="/" replace />;
};

const RideRequestCard = ({ data, BaseUrl, services, navigate, t, setIsBidding, ranningData }) => {
  const timeLeft = data.remaining_seconds !== undefined ? data.remaining_seconds : data.diff_second;
  const totalSeconds = data.total_seconds !== undefined ? data.total_seconds : data.diff_second;

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleDecline = () => {
    const uid = secureLocalStorage.getItem("Userid");
    const FindMrole = services.find((list) => list.name === data?.car_name);
    const SocketBody = {
      uid,
      id: data?.id,
      request_id: data?.request_id,
      m_role: FindMrole?.role,
    }
    console.log(SocketBody);
    socket.emit("Bidding_decline", SocketBody);
    setIsBidding(false);
  }

  const handleAccept = () => {
    const uid = secureLocalStorage.getItem("Userid");
    const FindMrole = services.find((list) => list.name === data?.car_name);

    const SocketBody = {
      uid,
      d_id: data?.id,
      request_id: data?.request_id,
      price: data?.price,
      m_role: FindMrole?.role,
    }

    navigate("/my-rides", { state: { d_id: data?.id, id: data?.request_id, status: 1 } });
    setIsBidding(false);
    socket.emit("Accept_Bidding", SocketBody);
  }

  return (
    <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-lg p-4 my-2">
      {Number(ranningData?.price) === Number(data?.price) && (
        <div className="mb-3 inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-[#4038ff] text-white select-none">
          {t("Your fare")}
        </div>
      )}

      {/* Top Section */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <ImageLoad src={BaseUrl + data.profile_image} CustomeStyle={"w-12 h-12 rounded-full overflow-hidden"} ImagStyle={"w-full h-full object-cover"} alt="profile" status={false} />
          <div>
            <h3 className="text-sm font-semibold capitalize">
              {data.first_name} {data.last_name}
            </h3>
            <p className="text-xs text-gray-500">{data.car_name}</p>
          </div>
        </div>

        <div className="text-[--bg-color] font-bold text-lg">
          {secureLocalStorage.getItem("currency") || "$"}{data.price}
        </div>
      </div>

      {/* Middle Section */}
      <div className="flex justify-between mt-3 text-sm text-gray-600">
        <div className='flex items-center gap-1'>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.25C7.175 2.25 3.25 6.175 3.25 11C3.25 16.118 7.94699 19.2199 11.055 21.2729L11.584 21.624C11.71 21.708 11.855 21.75 12 21.75C12.145 21.75 12.29 21.708 12.416 21.624L12.945 21.2729C16.053 19.2199 20.75 16.118 20.75 11C20.75 6.175 16.825 2.25 12 2.25ZM12.119 20.021L12 20.1001L11.881 20.021C8.871 18.033 4.75 15.311 4.75 11C4.75 7.002 8.002 3.75 12 3.75C15.998 3.75 19.25 7.002 19.25 11C19.25 15.311 15.128 18.034 12.119 20.021ZM12 7.75C10.208 7.75 8.75 9.208 8.75 11C8.75 12.792 10.208 14.25 12 14.25C13.792 14.25 15.25 12.792 15.25 11C15.25 9.208 13.792 7.75 12 7.75ZM12 12.75C11.035 12.75 10.25 11.965 10.25 11C10.25 10.035 11.035 9.25 12 9.25C12.965 9.25 13.75 10.035 13.75 11C13.75 11.965 12.965 12.75 12 12.75Z" fill="currentColor" />
          </svg>
          <span>{data.tot_km} {t("km")}</span>
        </div>
        <div className='flex items-center gap-1'>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.5979 7.96301L18.531 7.03003C18.824 6.73703 18.824 6.26199 18.531 5.96899C18.238 5.67599 17.763 5.67599 17.47 5.96899L16.4919 6.94702C15.1039 5.88702 13.377 5.25 11.5 5.25C6.951 5.25 3.25 8.951 3.25 13.5C3.25 18.049 6.951 21.75 11.5 21.75C16.049 21.75 19.75 18.049 19.75 13.5C19.75 11.368 18.9299 9.42901 17.5979 7.96301ZM11.5 20.25C7.778 20.25 4.75 17.222 4.75 13.5C4.75 9.778 7.778 6.75 11.5 6.75C15.222 6.75 18.25 9.778 18.25 13.5C18.25 17.222 15.222 20.25 11.5 20.25ZM13.95 14.4C14.281 14.649 14.3481 15.119 14.0991 15.45C13.9531 15.646 13.727 15.75 13.498 15.75C13.342 15.75 13.1841 15.701 13.0491 15.6L11.0491 14.1C10.8611 13.958 10.749 13.736 10.749 13.5V10C10.749 9.586 11.085 9.25 11.499 9.25C11.913 9.25 12.249 9.586 12.249 10V13.125L13.95 14.4ZM8.25 3C8.25 2.586 8.586 2.25 9 2.25H14C14.414 2.25 14.75 2.586 14.75 3C14.75 3.414 14.414 3.75 14 3.75H9C8.586 3.75 8.25 3.414 8.25 3Z" fill="currentColor" />
          </svg>
          <span>{data.tot_min} {t("min")}</span>
        </div>
        <div className='flex items-center gap-1'>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.8541 21.75C16.5651 21.75 16.275 21.681 16.006 21.54L12.1181 19.505C12.0441 19.466 11.956 19.466 11.882 19.505L7.997 21.538C7.378 21.862 6.6421 21.808 6.0761 21.396C5.5131 20.985 5.23508 20.304 5.35308 19.617L6.09405 15.316C6.10805 15.235 6.081 15.153 6.023 15.096L2.78203 11.95C2.30103 11.483 2.13009 10.796 2.33709 10.157C2.54409 9.51798 3.08601 9.06099 3.75201 8.96399L8.23406 8.31598C8.31606 8.30398 8.38605 8.25299 8.42205 8.17999L10.365 4.26202C10.675 3.63702 11.3011 3.24902 11.9991 3.24902C12.6971 3.24902 13.3231 3.63702 13.6331 4.26202L15.575 8.17902C15.612 8.25302 15.6821 8.30398 15.7641 8.31598L20.246 8.96399C20.912 9.05999 21.4541 9.51698 21.6611 10.157C21.8681 10.796 21.697 11.483 21.216 11.95L17.976 15.095C17.916 15.153 17.8901 15.235 17.9041 15.316L18.6451 19.619C18.7631 20.305 18.487 20.986 17.923 21.396C17.605 21.631 17.2301 21.75 16.8541 21.75ZM12.0001 17.976C12.2791 17.976 12.559 18.042 12.813 18.175L16.7011 20.21C16.8561 20.292 16.982 20.229 17.042 20.185C17.102 20.142 17.197 20.043 17.168 19.875L16.4271 15.571C16.3291 15.001 16.519 14.421 16.934 14.019L20.173 10.875C20.27 10.781 20.254 10.675 20.236 10.621C20.218 10.566 20.168 10.47 20.033 10.45L15.5511 9.802C14.9801 9.72 14.4871 9.36201 14.2321 8.84601L12.2911 4.92999C12.2141 4.77399 12.076 4.75098 12.001 4.75098C11.926 4.75098 11.7881 4.77399 11.7111 4.92999L9.7691 8.84698C9.5151 9.36198 9.0221 9.72 8.4511 9.802L3.96905 10.45C3.83405 10.47 3.78304 10.565 3.76604 10.621C3.74804 10.676 3.73303 10.781 3.82903 10.875L7.06902 14.02C7.48302 14.421 7.673 15.001 7.575 15.571L6.83404 19.872C6.80504 20.041 6.90111 20.14 6.96111 20.184C7.02311 20.228 7.14501 20.29 7.30401 20.209L11.189 18.176C11.441 18.043 11.7211 17.976 12.0001 17.976Z" fill="currentColor" />
          </svg>
          <span>{data.avg_star} ★ ({data.tot_review} reviews) • {data.completed_rides || 0} rides</span>
        </div>
      </div>

      {/* Timer */}
      <div className="mt-3 text-center">
        <p className={`font-semibold flex items-center justify-center gap-1 ${timeLeft <= 10 ? "text-red-600 animate-pulse" : "text-orange-500"}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.75 6.26196V4.5C19.75 3.259 18.74 2.25 17.5 2.25H6.5C5.26 2.25 4.25 3.259 4.25 4.5V6.2561C4.25 8.4361 5.17004 8.95989 6.14404 9.51489L10.5071 12L6.14404 14.4851C5.17004 15.0401 4.25 15.5639 4.25 17.7439V19.5C4.25 20.741 5.26 21.75 6.5 21.75H17.5C18.74 21.75 19.75 20.741 19.75 19.5V17.738C19.75 15.565 18.835 15.0411 17.865 14.4851L13.53 12L17.865 9.51489C18.835 8.95889 19.75 8.43496 19.75 6.26196ZM17.1189 15.7871C17.9879 16.2851 18.25 16.435 18.25 17.739V19.501C18.25 19.915 17.913 20.251 17.5 20.251H6.5C6.087 20.251 5.75 19.915 5.75 19.501V17.7451C5.75 16.4371 6.01399 16.287 6.88599 15.79L12.022 12.865L17.1189 15.7871ZM17.1189 8.21289L12.021 11.136L6.88501 8.21094C6.01301 7.71394 5.74902 7.5641 5.74902 6.2561V4.5C5.74902 4.086 6.08602 3.75 6.49902 3.75H17.499C17.912 3.75 18.249 4.086 18.249 4.5V6.26196C18.25 7.56496 17.9879 7.71489 17.1189 8.21289Z" fill="currentColor" />
          </svg>
          {timeLeft > 0 ? formatTime(timeLeft) : "Expired"}
        </p>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 h-2 rounded-full mt-2">
          <div
            className="bg-[--bg-color] h-2 rounded-full transition-all duration-1000"
            style={{
              width: `${(timeLeft / (Number(totalSeconds) || 60)) * 100}%`,
            }}
          ></div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={handleDecline}
          disabled={timeLeft <= 0}
          className={`flex-1 py-2 rounded-xl border text-sm font-medium transition
            ${timeLeft <= 0
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "border-red-500 text-red-500 hover:bg-red-50"}`}
        >
          {t("Decline")}
        </button>

        <button
          onClick={handleAccept}
          disabled={timeLeft <= 0}
          className={`flex-1 py-2 rounded-xl text-sm font-medium transition
            ${timeLeft <= 0
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[--bg-color] text-white hover:bg-[--bg-color-hover]"}`}
        >
          {t("Accept")}
        </button>
      </div>
    </div>
  );
};